import {cloneDeep} from '@shared/helpers/lang'
import {qs} from '@shared/helpers/queryString'

type Size = {
    left?: number
    top?: number
    width: number
    height: number
}

type OpenPopupParams = {
    /**
     * popup window 간 구분을 위한 고유 키
     */
    key: string
    /**
     * open할 popup url
     */
    url: string
    /**
     * popup size
     */
    size?: Size
    /**
     * popup center 에 위치할지 여부
     */
    shouldCenter?: boolean
    /**
     * popup window에 표시될 title
     */
    title?: string
    /**
     * 해당 팝업 이동 후 추가 동작을 위해 전달되어야할 쿼리 (ex. 팝업 message 전달, returnUrl로 사용할 url)
     */
    popupQuery?: {callback: string} | {rurl?: string; curl?: string; successUrl?: string; failUrl?: string}
    /**
     * 팝업을 띄운 페이지가 화면을 떠날때, 팝업의 처리방식 제어 필요
     * - 팝업이 유지되어야 하는 경우가 존재 (구매확정)
     */
    leaveType?: 'keep' | 'close'
}

let openerPageHidden = false
const popupMap: Record<string, {targetWindow: Window; shouldClose: boolean}> = {}

/**
 * popupOpen 시행하는 페이지가 hide 상태라면 동일 opener에 등록된 팝업 처리
 */
window.addEventListener('pagehide', () => {
    openerPageHidden = true
    Object.entries(popupMap).forEach(([key, {targetWindow, shouldClose}]) => {
        if (shouldClose) {
            targetWindow?.close()
            delete popupMap[key]
        }
    })
})

const focusOnPopup = (key: string) => {
    if (popupMap[key] && !popupMap[key].targetWindow?.closed) {
        popupMap[key]?.targetWindow?.focus()
    }
}

const isRegisteredWindow = ({key, targetWindow}: {key?: string; targetWindow: Window}) => {
    if (key) {
        if (popupMap[key] && popupMap[key].targetWindow) {
            return popupMap[key].targetWindow === targetWindow
        }

        return false
    }

    return Object.values(popupMap).some(({targetWindow: registeredWindow}) => registeredWindow === targetWindow)
}
const closeExistingPopup = ({key, url}: {key: string; url: string}): Promise<boolean> => {
    if (popupMap[key] && popupMap[key].targetWindow && !popupMap[key].targetWindow?.closed) {
        const currentHref = (() => {
            try {
                // 다른 도메인 창이 열려있으면 주소값에 접근 못하므로 체크한다
                const {href} = popupMap[key].targetWindow.location
                return href
            } catch {
                // CORS error
                return ''
            }
        })()

        // 제시한 키와 동일하게 등록되어있으나 현재 팝업주소가 새로운 요청과 불일치하는 팝업은 닫기 수행한다
        if (!currentHref || url !== currentHref) {
            popupMap[key]?.targetWindow?.close()
            // 맵에서 제거되기떄문에 팝업 매니저에서는 'POPUP_UNREGISTERED'로 처리한다
            delete popupMap[key]

            return new Promise((resolve) => {
                // 팝업 unload시 로직 수행 대비하여 300 milisec 대기
                setTimeout(() => resolve(true), 300)
            })
        }
    }

    return Promise.resolve(false)
}

const openPopup = ({
    key,
    url,
    size,
    title,
    shouldCenter,
    popupQuery,
    leaveType = 'close',
}: // 팝업 매니저 체크에서 closed로 체크되지 않았는데 닫힌 케이스는 rejection으로 처리된다
OpenPopupParams): Promise<'CLOSE' | void> => {
    // 이미 동일 키로 열려있는 popup 있다면 새로 열지 않고 focus
    if (popupMap[key] && !popupMap[key].targetWindow?.closed) {
        popupMap[key]?.targetWindow?.focus()
        return Promise.reject(new Error('POPUP_EXISTS'))
    }

    return new Promise((resolve, reject) => {
        const beforeCloseCallbacks: (() => void)[] = []
        const handlePopupClose = () => {
            beforeCloseCallbacks.forEach((callback) => {
                callback()
            })
        }
        const urlWithPopupQuery = getUrlByQuery({url, popupQuery})
        const popupWindow = window.open(
            urlWithPopupQuery,
            title,
            makeWindowFeatures(size, shouldCenter ? 'parent' : 'screen'),
        )

        // popup block 되어 열지 못한 케이스
        if (!popupWindow || popupWindow.closed) {
            window.alert('팝업이 차단되었습니다. 차단설정을 해제하신 후 다시 시도 바랍니다.')
            return reject(new Error('POPUP_BLOCK'))
        }

        popupMap[key] = {targetWindow: popupWindow, shouldClose: leaveType !== 'keep'}

        beforeCloseCallbacks.push(() => {
            delete popupMap[key]
        })

        // beforeunload 를 catch 할 수 없어, window 가 닫혔는지 주기적으로 검사한다.
        ;(async () => {
            for (let popupWindowClosed = false; ; !popupWindowClosed) {
                // 연속적으로 loop 돌지말고 100ms에 한번씩 체크하도록 대기
                await new Promise((_resolve) => setTimeout(_resolve, 100))

                // opener페이지가 닫혔으면 close 알려줄 대상이 사라졌으므로 그만 체크
                if (openerPageHidden) {
                    return reject(new Error('OPENER_CLOSED'))
                }

                // popup 관리 Map의 관리대상에서 벗어났다면 그만 체크
                if (!popupMap[key]) {
                    return reject(new Error('POPUP_UNREGISTERED'))
                }

                // 타겟 popup 닫혔으면 체크 loop 종료한다
                if (!popupWindow || popupWindow.closed) {
                    popupWindowClosed = true
                    // 닫기전에 postMessage 일어날 수 있으므로 대기
                    await new Promise((_resolve) => setTimeout(_resolve, 200))

                    handlePopupClose()
                    !openerPageHidden && resolve('CLOSE')
                }
            }
        })()
    })
}

function getUrlByQuery({
    url,
    popupQuery,
}: {
    url: string
    popupQuery?: {callback?: string; rurl?: string; curl?: string; successUrl?: string; failUrl?: string}
}) {
    const {url: copyUrl, query} = qs.parseUrl(url)

    const combinedQuery = cloneDeep(query ?? {})

    if (popupQuery) {
        Object.assign(combinedQuery, popupQuery)
    }

    return qs.stringifyUrl({
        url: copyUrl,
        query: combinedQuery,
    })
}

function makeWindowFeatures(size?: Size, center?: 'parent' | 'screen') {
    if (!size) {
        // 팝업이 아닌 새창으로 노출
        return ''
    }

    if (center === 'parent') {
        size.left = window.top?.outerWidth ?? 0 / 2 + (window.top?.screenX ?? 0) - size.width / 2
        size.top = window.top?.outerHeight ?? 0 / 2 + (window.top?.screenY ?? 0) - size.height / 2
    } else if (center === 'screen') {
        const screenLeft = window.screenLeft !== undefined ? window.screenLeft : 0
        const screenTop = window.screenTop !== undefined ? window.screenTop : 0

        const width = window.innerWidth
            ? window.innerWidth
            : document.documentElement.clientWidth
              ? document.documentElement.clientWidth
              : window.screen.width
        const height = window.innerHeight
            ? window.innerHeight
            : document.documentElement.clientHeight
              ? document.documentElement.clientHeight
              : window.screen.height

        size.left = width / 2 - size.width / 2 + screenLeft
        size.top = height / 2 - size.height / 2 + screenTop
    }

    const result: string[] = []

    return Object.keys(size)
        .reduce((arr, name) => {
            arr.push(`${name}=${size[name as keyof Size]}`)
            return arr
        }, result)
        .join(',')
}

export default openPopup
export {focusOnPopup, closeExistingPopup, isRegisteredWindow}
